/**
 * Building blocks for the Exceptions hub layout (`ExceptionsHub`).
 *
 * This module exports the reusable pieces:
 *   - `MetricCard` — compact summary card (label + value, optional caption / accent).
 *   - `BandDotTally` — inline dot tally `●N Critical · ●N Poor · …` used in the Health card.
 *   - `DomainHealthSummaryChip` — `MetricCard` wrapper that renders a `BandDotTally`.
 *   - `DomainHealthChip` — selected-area sparkline + trend + optional tally.
 *   - `SoftBandChip` — low-contrast band pill (used in the hub rail band indicator).
 *   - `ReportRow` — soft card for a report (type glyph + description, severity pill + quiet count).
 *   - `FilterChipStrip` + `FILTER_PREDICATES` — All / Needs attention / Good / No exceptions filter.
 *   - `DomainHealthTrend`, `SparklineDataPoint` — payload shapes returned by the per-area endpoints.
 *
 * `ExceptionType` is re-exported from the canonical API types module so this
 * file stays the single import surface for hub consumers.
 */
import React, { useMemo } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Typography,
  Stack,
  Paper,
  Chip,
} from '@mui/material'
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
} from '@mui/icons-material'
import { HealthScoreBadge } from './HealthScoreBadge'
import FpVisibilityIcon from './inspector/shared/FpVisibilityIcon'
import ExceptionTypeLabel from './shared/ExceptionTypeLabel'
import { LineChart, Line, ResponsiveContainer } from 'recharts'
import { poolwerxColors } from '../theme/colors'
import type { ExceptionType } from '../services/api'
import { bandHex, type HealthBand } from './inspector/dashboard/healthScore'

export type { ExceptionType }

export interface SparklineDataPoint {
  date: string
  score: number | null
  band: string
  exception_count: number
}

export interface DomainHealthTrend {
  current_score: number | null
  current_band: string
  current_color: string
  trend_direction: 'improving' | 'declining' | 'stable' | 'no_data'
  change_percent: number
  sparkline_data: SparklineDataPoint[]
  days: number
  data_points: number
}

const TrendIndicator: React.FC<{ direction: string; change: number }> = ({ direction, change }) => {
  const getIcon = () => {
    switch (direction) {
      case 'improving':
        return <TrendingDownIcon sx={{ fontSize: 16, color: '#4caf50' }} />
      case 'declining':
        return <TrendingUpIcon sx={{ fontSize: 16, color: '#f44336' }} />
      default:
        return <TrendingFlatIcon sx={{ fontSize: 16, color: '#9e9e9e' }} />
    }
  }
  const color =
    direction === 'improving' ? '#4caf50'
    : direction === 'declining' ? '#f44336'
    : '#9e9e9e'
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {getIcon()}
      <Typography variant="body2" sx={{ color, fontWeight: 600 }}>
        {Math.abs(change)}%
      </Typography>
    </Stack>
  )
}

const PANEL_BORDER = 'grey.200'

interface MetricCardProps {
  label: string
  /** Primary value (string or pre-formatted number). Pass a React node for custom layouts. */
  value: React.ReactNode
  /** Below the value — caption-styled. */
  caption?: React.ReactNode
  /** Accent color for the left bar. Defaults to teal. Set to `null` for no accent. */
  accent?: string | null
  /** Override icon at the top-right of the card. */
  trailing?: React.ReactNode
}

export const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  caption,
  accent = null,
  trailing,
}) => (
  <Paper
    variant="outlined"
    sx={{
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      px: 2.25,
      py: 2,
      borderRadius: 2,
      borderColor: PANEL_BORDER,
      position: 'relative',
      overflow: 'hidden',
      ...(accent && {
        borderLeft: `3px solid ${accent}`,
        pl: 2,
      }),
    }}
  >
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ letterSpacing: 0.6, fontWeight: 600, lineHeight: 1.4, display: 'block' }}
        >
          {label}
        </Typography>
        <Box sx={{ mt: 0.25 }}>
          {typeof value === 'string' || typeof value === 'number' ? (
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.15 }}>
              {value}
            </Typography>
          ) : (
            value
          )}
        </Box>
        {caption && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {caption}
          </Typography>
        )}
      </Box>
      {trailing && <Box sx={{ flexShrink: 0 }}>{trailing}</Box>}
    </Stack>
  </Paper>
)

export const DomainHealthChip: React.FC<{ trend: DomainHealthTrend; trailing?: React.ReactNode }> = ({
  trend,
  trailing,
}) => {
  const sparklineData = useMemo(
    () => trend.sparkline_data.map((p) => ({ ...p, score: p.score ?? 0 })),
    [trend.sparkline_data],
  )
  const stroke = trend.current_color === 'black' ? '#333' : trend.current_color
  const hasSparkline = trend.sparkline_data.length > 1
  const hasScore = trend.current_score !== null

  return (
    <MetricCard
      label="Health"
      trailing={trailing}
      value={
        <Stack spacing={1}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
          >
            {hasScore ? (
              <HealthScoreBadge
                band={trend.current_band as any}
                color={trend.current_color as any}
                score={trend.current_score}
                size="small"
              />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                Not scored
              </Typography>
            )}
            {hasScore && (
              <TrendIndicator direction={trend.trend_direction} change={trend.change_percent} />
            )}
          </Stack>
          {hasSparkline && (
            <Box
              title={`${trend.days}-day trend`}
              sx={{ width: '100%', height: 28 }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={sparklineData}>
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke={stroke}
                    strokeWidth={1.75}
                    dot={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Stack>
      }
    />
  )
}

export interface DomainHealthSummary {
  Good: number
  Watch: number
  Poor: number
  Critical: number
}

const BAND_ORDER: Array<keyof DomainHealthSummary> = ['Critical', 'Poor', 'Watch', 'Good']

interface DomainHealthSummaryChipProps {
  summary: DomainHealthSummary
  lastValidatedLabel?: string | null
}

/** Inline dot tally — e.g. `●2 Critical · ●1 Watch · ●3 Good`. */
export const BandDotTally: React.FC<{ summary: DomainHealthSummary; dense?: boolean }> = ({
  summary,
  dense = false,
}) => {
  const bands = BAND_ORDER.filter((b) => summary[b] > 0)
  if (bands.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Not scored
      </Typography>
    )
  }
  const fontVariant = dense ? 'caption' : 'body2'
  const dotSize = dense ? 6 : 8
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
      {bands.map((band, i) => (
        <Stack key={band} direction="row" alignItems="center" spacing={0.5}>
          {i > 0 && (
            <Typography variant="caption" color="text.disabled" sx={{ mr: 0.25 }}>
              ·
            </Typography>
          )}
          <Box
            sx={{
              width: dotSize,
              height: dotSize,
              borderRadius: '50%',
              bgcolor: bandHex(band as HealthBand),
              flexShrink: 0,
            }}
          />
          <Typography variant={fontVariant} sx={{ fontWeight: 700 }}>
            {summary[band]}
          </Typography>
          <Typography variant={fontVariant} color="text.secondary">
            {band}
          </Typography>
        </Stack>
      ))}
    </Stack>
  )
}

export const DomainHealthSummaryChip: React.FC<DomainHealthSummaryChipProps> = ({
  summary,
  lastValidatedLabel,
}) => (
  <MetricCard
    label="Health"
    value={<BandDotTally summary={summary} />}
    caption={lastValidatedLabel || undefined}
  />
)

interface SoftBandChipProps {
  band: HealthBand
  /** Override label (defaults to the band name). */
  label?: string
}

export const SoftBandChip: React.FC<SoftBandChipProps> = ({ band, label }) => {
  const hex = bandHex(band)
  return (
    <Box
      component="span"
      sx={{
        px: 0.75,
        py: 0.125,
        borderRadius: 1,
        bgcolor: `${hex}15`,
        color: hex,
        fontSize: '0.68rem',
        fontWeight: 700,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        lineHeight: 1.6,
        flexShrink: 0,
        whiteSpace: 'nowrap',
      }}
    >
      {label ?? band}
    </Box>
  )
}

export type FilterKey = 'all' | 'needs-attention' | 'good' | 'no-exceptions'

export const FILTER_PREDICATES: Record<FilterKey, (t: ExceptionType) => boolean> = {
  'all': () => true,
  'needs-attention': (t) =>
    (!!t.health_score?.band && ['Watch', 'Poor', 'Critical'].includes(t.health_score.band))
    || (!t.health_score && t.count > 0),
  'good': (t) => t.health_score?.band === 'Good',
  'no-exceptions': (t) => t.count === 0,
}

export const FILTER_LABELS: Record<FilterKey, string> = {
  'all': 'All',
  'needs-attention': 'Needs attention',
  'good': 'Good',
  'no-exceptions': 'No exceptions',
}

interface FilterChipStripProps {
  active: FilterKey
  onChange: (next: FilterKey) => void
  counts: Record<FilterKey, number>
}

export const FilterChipStrip: React.FC<FilterChipStripProps> = ({ active, onChange, counts }) => {
  const keys: FilterKey[] = ['all', 'needs-attention', 'good', 'no-exceptions']
  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
      {keys.map((key) => {
        if (key !== 'all' && counts[key] === 0) return null
        const isActive = key === active
        return (
          <Chip
            key={key}
            size="small"
            variant="outlined"
            label={`${FILTER_LABELS[key]} ${counts[key].toLocaleString()}`}
            onClick={() => onChange(key)}
            sx={{
              letterSpacing: 0.2,
              ...(isActive
                ? {
                    borderColor: poolwerxColors.teal,
                    color: poolwerxColors.teal,
                    fontWeight: 700,
                    bgcolor: 'transparent',
                  }
                : {
                    borderColor: 'grey.300',
                    color: 'text.secondary',
                    fontWeight: 600,
                  }),
            }}
          />
        )
      })}
    </Stack>
  )
}

// Report-row severity pills draw from the shared MUTED band palette
// (`bandHex` -> BAND_HEX in utils/exceptionSeverity) - the same registry the
// area rail and the dashboard read - so a row's severity reads identically to
// its rail band (Critical dark, Poor red, Watch orange). Good and unrated rows
// render no pill at all (a healthy / unscored row needs no signal).
const REPORT_SEVERITY_BANDS: HealthBand[] = ['Critical', 'Poor', 'Watch']

/**
 * Severity pill - a soft-tinted coloured pill for the priority bands only
 * (Critical / Poor / Watch). Good and unrated rows render nothing (no
 * placeholder), so the count sits flush to the right edge on those rows.
 */
const SeverityChip: React.FC<{ band?: HealthBand }> = ({ band }) => {
  if (!band || !REPORT_SEVERITY_BANDS.includes(band)) return null
  const hex = bandHex(band)
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 0.875,
        py: 0.25,
        borderRadius: 1,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        fontSize: '0.72rem',
        fontWeight: 700,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        color: hex,
        bgcolor: `${hex}1F`,
      }}
    >
      {band}
    </Box>
  )
}

interface ReportRowProps {
  type: ExceptionType
  to: string
}

export const ReportRow: React.FC<ReportRowProps> = ({ type, to }) => {
  const isZero = type.count === 0
  const scoreBand = type.health_score?.band as HealthBand | undefined
  // Soft card matching the Platform Reports list (RowLink): subtle white
  // surface + light border + hover lift. Zero-count rows fade the whole card
  // (still a live link - right-click / open-in-new-tab intact).
  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 1,
        borderRadius: 2,
        borderColor: 'grey.200',
        transition: 'border-color 0.15s, background-color 0.15s',
        '&:hover': {
          borderColor: 'primary.main',
          bgcolor: 'action.hover',
          '& .report-name': { color: poolwerxColors.teal },
        },
      }}
    >
      <Box
        component={RouterLink}
        to={to}
        sx={{
          display: 'flex',
          alignItems: 'center',
          pr: 2,
          py: 1.5,
          textDecoration: 'none',
          color: 'text.primary',
          // All-clear (0-count) rows read as quiet background context.
          opacity: isZero ? 0.5 : 1,
        }}
      >
        {/* Fixed ~42px type-marker gutter: the tinted classification glyph,
            aligned to the title line (not centred across the card). Same icon /
            colour / size as the type dropdown menu rows. Empty (slot reserved)
            when a row has no type. */}
        <Box
          sx={{
            width: 42,
            flexShrink: 0,
            alignSelf: 'flex-start',
            mt: '2px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <ExceptionTypeLabel
            variant="icon"
            exceptionType={type.exception_type}
            iconSize={18}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            className="report-name"
            variant="body2"
            sx={{
              fontSize: '0.95rem',
              fontWeight: 600,
              color: isZero ? 'text.secondary' : 'text.primary',
              transition: 'color 0.15s',
              mb: 0.25,
            }}
            noWrap
          >
            {type.name}
          </Typography>
          {type.overview && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', fontSize: '0.9rem' }}
            >
              {type.overview}
            </Typography>
          )}
        </Box>
        {/* Right rail: [PSC Only?] severity count, all right-aligned. The card
            itself is the link (no chevron). */}
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexShrink: 0, pl: 2 }}>
          <FpVisibilityIcon
            kind="exception"
            visibleToRoles={type.visible_to_roles}
            variant="chip"
          />
          <SeverityChip band={scoreBand} />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: isZero ? 'text.secondary' : 'text.primary',
              fontVariantNumeric: 'tabular-nums',
              textAlign: 'right',
              minWidth: 44,
              flexShrink: 0,
            }}
          >
            {type.count.toLocaleString()}
          </Typography>
        </Stack>
      </Box>
    </Paper>
  )
}
